// React & Hooks
import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { deleteFleetVehicleFireExtinguisherAPI, getFleetVehicleFireExtinguishersAPI } from "services/api/fleet/vehicles/fireextinguishers/api";
import { FleetVehicleFireExtinguisherRow } from "services/api/fleet/vehicles/fireextinguishers/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Search & Pagination
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Navigation
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// UI Components
import { NoResults } from "ui/components/NoResults";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { Button } from "ui/components/Button/Button";

// Page Titles



// Local Components & Types

import VehicleFireExtinguisherEditCard from "../VehicleFireExtinguisherEditCard/VehicleFireExtinguisherEditCard";
import FireExtinguisherSearchResults from "./components/VehicleFireExtinguisherSearchResults/FireExtinguisherSearchResults";
import { VehicleFireExtinguisherCardProps } from "./types";
import { Dialog } from "ui/components/Dialog";

const VehicleFireExtinguisherCard: React.FC<VehicleFireExtinguisherCardProps> = (props) => {
  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleFireExtinguisherRow>
  >(initialDataWithPagination);

  const [fireExtinguisherId, setFireExtinguisherId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleFireExtinguishersAPI(vehicleId, pagination);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setFireExtinguisherId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setFireExtinguisherId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  };

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);

    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fetchSearchResult]);


  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setFireExtinguisherId(id);
    setOpenDialog(true);
  }

  const handleFireExtinguisherDeleteDialogConfirm = useCallback(async () => {
    if (!fireExtinguisherId) return;

    try {
      const deleted = await deleteFleetVehicleFireExtinguisherAPI(
        fireExtinguisherId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setFireExtinguisherId("");
    } catch (err) {
      console.error("Greška prilikom brisanja vatrogasnog aparata:", err);
    }
  }, [fireExtinguisherId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleFireExtinguisherDeleteDialogClose = useCallback(() => {
    setFireExtinguisherId("");
    setOpenDialog(false);
  }, []);


  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3  ">
        <div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase ">

            </div>
            <div className="flex items-center gap-2 px-3 my-3 ">
              <div className="">
                <ButtonLink
                  size="small"
                  variant="warning"
                  children={t("admin.fleet.vehicle.fireextinguisher.detail")}
                  to={transformRoute(
                    FleetVehicleRoutes.FleetVehicleFireExtinguisherSearchPageRouter,
                    {
                      id: fleetVehicleDetail.id,
                    }
                  )}
                  startIcon="/media/icons/search.svg"
                />
              </div>
              <div>
                <Button
                  size="small"
                  variant="secondary"
                  children={t("admin.fleet.vehicle.fireextinguisher.add")}
                  onClick={handleAddNew}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
          </div>
          <div className="m-[-1px]">
            {!isLoading && searchResults.pagination.totalPages === 0 ? (
              <NoResults
                message={t("admin.fleet.vehicle.fireextinguisher.search.noresults")}
              />
            ) : (
              <FireExtinguisherSearchResults
                searchResults={searchResults}
                selectedRows={selectedRows}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                onPaginationChange={handlePaginationChange}
                onSelectedChange={handleSelectedChange}
                onDeleteClick={handleDeleteClick}
              />
            )}
          </div>
        </div>
      </div>

      {showAddNewModal && (
        <VehicleFireExtinguisherEditCard
          show={showAddNewModal}
          vehicleId={fleetVehicleDetail.id}
          fireExtinguisherId={fireExtinguisherId}
          onSuccess={handleEditSuccess}
          onClose={() => setShowAddNewModal(false)}
        />
      )}
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.fireextinguisher.dialog.delete.canceltext"
        )}
        onConfirm={handleFireExtinguisherDeleteDialogConfirm}
        onCancel={handleFireExtinguisherDeleteDialogClose}
      />
    </>
  );
};

export default memo(VehicleFireExtinguisherCard);
